/**
 * Contract filter AST <-> AG Grid filter model.
 *
 * The contract deliberately carries its *own* AST rather than AG Grid's filter
 * model (D18): the grid is a renderer, so a saved filter must outlive it. This
 * module is the only place the two vocabularies meet — swapping in Enterprise's
 * Advanced Filter, or a different grid entirely, replaces this file and nothing
 * else.
 *
 * The contract AST is strictly more expressive than AG Grid Community's model:
 *
 *   contract              Community filter model
 *   ------------------    -------------------------------------------------
 *   {all: [...]}          one entry per field, ANDed (the model's only join)
 *   {any: [...]}          per-field `operator: "OR"`, max two conditions
 *   nested groups         no equivalent
 *
 * Rather than silently dropping what Community can't express, {@link toGridModel}
 * reports it: `unsupported` lists every node that didn't survive, so the caller
 * can warn instead of showing rows that quietly ignore half the filter.
 */

import type {
  ScreenerCompareOp,
  ScreenerFilterCondition,
  ScreenerFilterGroup,
  ScreenerFilterNode,
  ScreenerScalar,
} from "@qufolio/model";
import type { ScreenerFieldType } from "@qufolio/transport";

/** One AG Grid condition (`agTextColumnFilter` / `agNumberColumnFilter` shape). */
export interface GridCondition {
  filterType: "text" | "number" | "date";
  type: string;
  filter?: ScreenerScalar;
  filterTo?: ScreenerScalar;
}

/** Per-field entry: a single condition, or two joined by AND/OR. */
export type GridFilterEntry =
  | GridCondition
  | {
      filterType: "text" | "number" | "date";
      operator: "AND" | "OR";
      conditions: GridCondition[];
    };

export type GridFilterModel = Record<string, GridFilterEntry>;

export interface ToGridModelResult {
  model: GridFilterModel;
  /** Human-readable notes on anything Community could not represent. */
  unsupported: string[];
}

const isGroup = (node: ScreenerFilterNode): node is ScreenerFilterGroup =>
  "children" in node && Array.isArray((node as ScreenerFilterGroup).children);

// ── contract op <-> AG Grid type ────────────────────────────────────────────

const OP_TO_GRID: Record<ScreenerCompareOp, string> = {
  eq: "equals",
  ne: "notEqual",
  gt: "greaterThan",
  gte: "greaterThanOrEqual",
  lt: "lessThan",
  lte: "lessThanOrEqual",
  between: "inRange",
  contains: "contains",
  notContains: "notContains",
  startsWith: "startsWith",
  endsWith: "endsWith",
  in: "in", // not a Community type; reported as unsupported
  blank: "blank",
  notBlank: "notBlank",
};

const GRID_TO_OP: Record<string, ScreenerCompareOp> = {
  equals: "eq",
  notEqual: "ne",
  greaterThan: "gt",
  greaterThanOrEqual: "gte",
  lessThan: "lt",
  lessThanOrEqual: "lte",
  inRange: "between",
  contains: "contains",
  notContains: "notContains",
  startsWith: "startsWith",
  endsWith: "endsWith",
  blank: "blank",
  notBlank: "notBlank",
};

/** Community has no set filter, so `in` has no representation. */
const UNSUPPORTED_OPS = new Set<ScreenerCompareOp>(["in"]);

function filterTypeFor(type: ScreenerFieldType | undefined): "text" | "number" | "date" {
  if (type === "number") return "number";
  if (type === "date") return "date";
  return "text";
}

function toCondition(
  condition: ScreenerFilterCondition,
  fieldTypes: Map<string, ScreenerFieldType>,
): GridCondition {
  const out: GridCondition = {
    filterType: filterTypeFor(fieldTypes.get(condition.field)),
    type: OP_TO_GRID[condition.op],
  };
  if (condition.value !== undefined) out.filter = condition.value;
  if (condition.valueTo !== undefined) out.filterTo = condition.valueTo;
  return out;
}

/**
 * Translate the contract AST into a Community filter model.
 *
 * Flattens the tree by field: conditions on the same field become one entry
 * (joined by the group's operator), conditions on different fields are ANDed —
 * which is the only join Community's model has across fields.
 */
export function toGridModel(
  filter: ScreenerFilterNode | undefined,
  fields: readonly { name: string; type: ScreenerFieldType }[] = [],
): ToGridModelResult {
  const fieldTypes = new Map(fields.map((f) => [f.name, f.type]));
  const model: GridFilterModel = {};
  const unsupported: string[] = [];
  if (!filter) return { model, unsupported };

  // field -> conditions, plus the operator implied by the group they came from.
  const byField = new Map<string, { op: "AND" | "OR"; conditions: GridCondition[] }>();

  const walk = (node: ScreenerFilterNode, join: "AND" | "OR", depth: number): void => {
    if (isGroup(node)) {
      const childJoin = node.op === "any" ? "OR" : "AND";
      if (depth > 0 && childJoin !== join) {
        unsupported.push(
          `nested ${node.op} group at depth ${depth} — Community cannot express mixed AND/OR nesting`,
        );
      }
      for (const child of node.children) walk(child, childJoin, depth + 1);
      return;
    }

    // A ScanScript condition has no grid-model form. It is still applied: the
    // screener evaluates the filter itself (`filter-eval.ts`); this only says
    // the grid's own filter menu cannot show it.
    if ("kind" in node) {
      unsupported.push(`ScanScript condition — applied by the screener, not shown in the column menu`);
      return;
    }

    if (UNSUPPORTED_OPS.has(node.op)) {
      unsupported.push(
        `${node.field} ${node.op} — Community has no set filter (Enterprise only)`,
      );
      return;
    }

    const entry = byField.get(node.field) ?? { op: join, conditions: [] };
    entry.op = join;
    entry.conditions.push(toCondition(node, fieldTypes));
    byField.set(node.field, entry);
  };

  walk(filter, filter && isGroup(filter) && filter.op === "any" ? "OR" : "AND", 0);

  for (const [field, { op, conditions }] of byField) {
    if (conditions.length === 1) {
      model[field] = conditions[0]!;
      continue;
    }
    if (conditions.length > 2) {
      unsupported.push(
        `${field} has ${conditions.length} conditions — Community supports at most 2 per column`,
      );
    }
    model[field] = {
      filterType: conditions[0]!.filterType,
      operator: op,
      conditions: conditions.slice(0, 2),
    };
  }

  return { model, unsupported };
}

/**
 * Translate a Community filter model back into the contract AST.
 *
 * Always produces an `all` group over per-field nodes — a faithful inverse,
 * since AND across fields is what the model means. Returns `undefined` for an
 * empty model so a cleared filter round-trips to an absent one.
 */
export function fromGridModel(model: GridFilterModel | null | undefined): ScreenerFilterNode | undefined {
  if (!model) return undefined;
  const entries = Object.entries(model);
  if (entries.length === 0) return undefined;

  const nodes: ScreenerFilterNode[] = [];

  for (const [field, entry] of entries) {
    if ("conditions" in entry && Array.isArray(entry.conditions)) {
      const children = entry.conditions
        .map((c) => conditionToNode(field, c))
        .filter((n): n is ScreenerFilterCondition => n !== undefined);
      if (children.length === 0) continue;
      nodes.push(
        children.length === 1
          ? children[0]!
          : { op: entry.operator === "OR" ? "any" : "all", children },
      );
      continue;
    }
    const node = conditionToNode(field, entry as GridCondition);
    if (node) nodes.push(node);
  }

  if (nodes.length === 0) return undefined;
  if (nodes.length === 1) return nodes[0]!;
  return { op: "all", children: nodes };
}

function conditionToNode(
  field: string,
  condition: GridCondition,
): ScreenerFilterCondition | undefined {
  const op = GRID_TO_OP[condition.type];
  if (!op) return undefined;

  const node: ScreenerFilterCondition = { field, op };
  if (condition.filter !== undefined) node.value = condition.filter;
  if (condition.filterTo !== undefined) node.valueTo = condition.filterTo;
  return node;
}
